// The org_type Postgres enum (migration 20260215011610). Entity kind only —
// this NEVER feeds footprint derivation. Used by the Console edit-on-behalf
// select (and, once wired, the onboarding wizard dropdown) so callers offer
// exactly the enum's values and can never write an invalid one.
export const ORG_TYPE_VALUES = ["franchise", "corporate", "independent", "team"] as const;

export type OrgType = (typeof ORG_TYPE_VALUES)[number];

export const ORG_TYPE_OPTIONS: { value: OrgType; label: string }[] = [
  { value: "franchise", label: "Franchise" },
  { value: "corporate", label: "Corporate" },
  { value: "independent", label: "Independent" },
  { value: "team", label: "Team" },
];

/**
 * What each type means, shown under every type picker. A profile is for the
 * unit that sets its agents' deal (a team, an office or the brokerage), never
 * the national brand: a "powered by eXp" team is a Team, not eXp.
 */
export const ORG_TYPE_DESCRIPTIONS: Record<OrgType, string> = {
  independent: "Your own brand, not part of a national one.",
  franchise: "Locally owned, operating under a national brand (like a RE/MAX or Century 21 office).",
  corporate: "Owned and run by the national brand itself (like a Coldwell Banker Realty office).",
  team: "A group inside a brokerage that sets its own deal for its agents, including teams \"powered by\" eXp, Real or Compass.",
};

/** The one rule that picks which unit gets a profile. */
export const ORG_TYPE_GUIDANCE =
  "Set up the profile for whoever sets your agents' deal: your team, your office or your brokerage, not the national brand.";

/** The description for a stored type, or null for an empty or unknown value. */
export function orgTypeDescription(value: unknown): string | null {
  return (ORG_TYPE_VALUES as readonly unknown[]).includes(value) ? ORG_TYPE_DESCRIPTIONS[value as OrgType] : null;
}

/**
 * The plain noun for an organization in owner-facing copy: a team is a
 * "team", never a "brokerage". Franchise, corporate, independent, empty and
 * unknown values all read as "brokerage".
 */
export function orgKindNoun(type: unknown): "team" | "brokerage" {
  return type === "team" ? "team" : "brokerage";
}

/** ORG_TYPE_OPTIONS as a value→label lookup, for sites that only need the label. */
export const ORG_TYPE_LABELS: Record<OrgType, string> = Object.fromEntries(
  ORG_TYPE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<OrgType, string>;
